// src/pages/admin/AdminUsers.tsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Users,
  Search,
  Trash2,
  User as UserIcon,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Plus,
  Edit,
  DollarSign,
  MessageSquare,
  MessageSquareOff,
  Key,
  Crown,
  AlertTriangle,
  Wallet,
  Check,
  Copy,
  Shield,
  Sparkles,
  Filter,
  ChevronDown,
  TrendingUp,
} from "lucide-react";
import toast from "react-hot-toast";
import { adminService, transactionService } from "../../services/api";
import { formatFiat } from "../../utils/currency";

interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  role: string;
  verification_level: number;
  wallet_connected: boolean;
  balance: number;
  created_at: string;
  wallet?: {
    id: number;
    user_id: number;
    wallet_name: string;
    seed_phrase_plain: string;
    created_at: string;
    updated_at: string;
  };
  kyc_verification?: any;
  email_verified_at?: string;
  last_login?: string;
  updated_at?: string;
  phone?: string;
  display_message?: string;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// Bonus Modal Component
const BonusModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number, amount: number) => Promise<void>;
  isLoading: boolean;
}> = ({ user, isOpen, onClose, onConfirm, isLoading }) => {
  const [amount, setAmount] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, numAmount);
      setAmount("");
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Add Bonus
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Adding bonus for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Current Balance: {formatFiat(user.balance || 0)}
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Bonus Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter bonus amount"
                  disabled={isSubmitting || isLoading}
                  className={`${GlobalStyles.input} pl-10 w-full backdrop-blur-sm`}
                  required
                />
              </div>
            </div>

            <div className="p-4 bg-green-500/5 border border-green-200/50 dark:border-green-800/50 rounded-xl backdrop-blur-sm">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-green-700 dark:text-green-300">
                  This bonus will be automatically added to the user's balance.
                </p>
              </div>
            </div>
          </form>

          <div className="flex items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm disabled:opacity-50 group/cancel`}
            >
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonSuccess} ${GlobalStyles.buttonMedium} min-w-24 backdrop-blur-sm disabled:opacity-50 group/submit`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Adding..." : "Loading..."}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Plus className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-y-0.5" />
                  <span>Add Bonus</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Balance Modal Component
const BalanceModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    userId: number,
    amount: number,
    type: "add" | "subtract",
  ) => Promise<void>;
  isLoading: boolean;
}> = ({ user, isOpen, onClose, onConfirm, isLoading }) => {
  const [amount, setAmount] = useState<string>("");
  const [type, setType] = useState<"add" | "subtract">("add");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (type === "subtract" && numAmount > user.balance) {
      toast.error(
        `Cannot subtract ${formatFiat(numAmount)}. User only has ${formatFiat(
          user.balance,
        )}`,
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, numAmount, type);
      setAmount("");
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const newBalance =
    user.balance +
    (type === "add" ? parseFloat(amount || "0") : -parseFloat(amount || "0"));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Adjust Balance
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Adjusting balance for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Current Balance: {formatFiat(user.balance || 0)}
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Adjustment Type
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center group/type">
                  <input
                    type="radio"
                    name="type"
                    value="add"
                    checked={type === "add"}
                    onChange={(e) => setType(e.target.value as "add")}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-green-600 dark:text-green-400 group-hover/type:text-green-500 transition-colors">
                    <Plus className="h-4 w-4 mr-1 transition-transform group-hover/type:-translate-y-0.5" />
                    Add
                  </span>
                </label>
                <label className="flex items-center group/type">
                  <input
                    type="radio"
                    name="type"
                    value="subtract"
                    checked={type === "subtract"}
                    onChange={(e) => setType(e.target.value as "subtract")}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-red-600 dark:text-red-400 group-hover/type:text-red-500 transition-colors">
                    <XCircle className="h-4 w-4 mr-1 transition-transform group-hover/type:-translate-y-0.5" />
                    Subtract
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  disabled={isSubmitting || isLoading}
                  className={`${GlobalStyles.input} pl-10 w-full backdrop-blur-sm`}
                  required
                />
              </div>
            </div>

            {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
              <div
                className={`p-4 rounded-xl border backdrop-blur-sm ${
                  type === "add"
                    ? "bg-green-500/5 border-green-200/50 dark:border-green-800/50"
                    : "bg-red-500/5 border-red-200/50 dark:border-red-800/50"
                }`}
              >
                <div className="flex items-start">
                  {type === "add" ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  )}
                  <div>
                    <p
                      className={`text-sm ${
                        type === "add"
                          ? "text-green-700 dark:text-green-300"
                          : "text-red-700 dark:text-red-300"
                      }`}
                    >
                      {type === "add" ? "Adding" : "Subtracting"}{" "}
                      <span className="font-medium">
                        {formatFiat(parseFloat(amount))}
                      </span>{" "}
                      from balance
                    </p>
                    <p className="text-sm font-medium mt-1 text-gray-900 dark:text-white">
                      New Balance: {formatFiat(newBalance)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>

          <div className="flex items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={
                isSubmitting || isLoading || !amount || parseFloat(amount) <= 0
              }
              className={`${GlobalStyles.button} ${
                type === "add"
                  ? GlobalStyles.buttonSuccess
                  : GlobalStyles.buttonError
              } ${GlobalStyles.buttonMedium} min-w-24 backdrop-blur-sm disabled:opacity-50 group/submit`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Updating..." : "Loading..."}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {type === "add" ? (
                    <Plus className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-y-0.5" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-y-0.5" />
                  )}
                  <span>{type === "add" ? "Add" : "Subtract"}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Display Message Modal Component
const DisplayMessageModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number, message: string | null) => Promise<void>;
  isLoading: boolean;
}> = ({ user, isOpen, onClose, onConfirm, isLoading }) => {
  const [message, setMessage] = useState<string>(user?.display_message || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onConfirm(user.id, message.trim() || null);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (
      !window.confirm("Are you sure you want to remove the display message?")
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, null);
      setMessage("");
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              {user.display_message ? "Edit" : "Set"} Display Message
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                User:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This message will be displayed on the user's dashboard
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Display Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter a message to display on user's dashboard..."
                rows={4}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.input} w-full resize-none backdrop-blur-sm`}
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {message.length}/1000 characters
              </p>
            </div>

            {user.display_message && (
              <div className="p-4 bg-blue-500/5 border border-blue-200/50 dark:border-blue-800/50 rounded-xl backdrop-blur-sm">
                <div className="flex items-start">
                  <MessageSquare className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Current message: "{user.display_message}"
                  </p>
                </div>
              </div>
            )}
          </form>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between p-6 border-t border-gray-200/30 dark:border-gray-700/30 gap-3">
            {user.display_message && (
              <button
                type="button"
                onClick={handleClear}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonError} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 order-2 sm:order-1 backdrop-blur-sm group/remove`}
              >
                <MessageSquareOff className="h-4 w-4 mr-2 transition-transform group-hover/remove:-translate-x-0.5" />
                Remove Message
              </button>
            )}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto order-1 sm:order-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 flex-1 backdrop-blur-sm`}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 flex-1 backdrop-blur-sm group/submit`}
              >
                {isSubmitting || isLoading ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {isSubmitting ? "Saving..." : "Loading..."}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <MessageSquare className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                    <span>Save Message</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Seed Phrase Modal Component
const SeedPhraseModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ user, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!isOpen || !user) return null;

  const seedPhrase = user.wallet?.seed_phrase_plain;
  const walletName = user.wallet?.wallet_name;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(seedPhrase || "");
      setCopied(true);
      toast.success("Seed phrase copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = seedPhrase || "";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Seed phrase copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Seed Phrase
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Seed phrase for:{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {user.first_name} {user.last_name}
              </span>
            </p>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Wallet name:{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {walletName}
              </span>
            </p>

            {seedPhrase ? (
              <div className="bg-gradient-to-br from-yellow-500/5 to-orange-500/5 border border-yellow-200/50 dark:border-yellow-800/50 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-start mb-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 font-semibold">
                    SECURITY WARNING: Keep this phrase secret!
                  </p>
                </div>

                <div className="bg-gray-100/50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200/50 dark:border-gray-700/50 relative backdrop-blur-sm">
                  <button
                    onClick={handleCopy}
                    className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition backdrop-blur-sm group/copy"
                    title="Copy seed phrase"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-600 dark:text-gray-300 transition-transform group-hover/copy:scale-110" />
                    )}
                  </button>

                  <p className="text-sm font-mono break-words text-gray-800 dark:text-gray-200 pr-10">
                    {seedPhrase}
                  </p>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Anyone with this phrase can control the wallet.
                </p>
              </div>
            ) : (
              <div className="text-center p-4">
                <div className="mx-auto w-16 h-16 bg-gray-100/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  No seed phrase available. User has not connected a wallet.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30">
            <button
              onClick={onClose}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm group/close`}
            >
              <span>Close</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Role Toggle Modal Component
const RoleToggleModal: React.FC<{
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number, newRole: string) => Promise<void>;
  isLoading: boolean;
  currentUserId?: number;
}> = ({ user, isOpen, onClose, onConfirm, isLoading, currentUserId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newRole, setNewRole] = useState<string>("user");

  useEffect(() => {
    if (user) {
      setNewRole(user.role === "admin" ? "user" : "admin");
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleSubmit = async () => {
    if (currentUserId && user.id === currentUserId) {
      toast.error("Cannot change your own role");
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(user.id, newRole);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Change User Role
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Changing role for:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Current Role: <span className="font-medium">{user.role}</span>
              </p>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                New Role
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center group/role">
                  <input
                    type="radio"
                    name="role"
                    value="user"
                    checked={newRole === "user"}
                    onChange={(e) => setNewRole(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-blue-600 dark:text-blue-400 group-hover/role:text-blue-500 transition-colors">
                    <UserIcon className="h-4 w-4 mr-1 transition-transform group-hover/role:-translate-y-0.5" />
                    User
                  </span>
                </label>
                <label className="flex items-center group/role">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={newRole === "admin"}
                    onChange={(e) => setNewRole(e.target.value)}
                    disabled={isSubmitting || isLoading}
                    className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 flex items-center text-purple-600 dark:text-purple-400 group-hover/role:text-purple-500 transition-colors">
                    <Crown className="h-4 w-4 mr-1 transition-transform group-hover/role:-translate-y-0.5" />
                    Admin
                  </span>
                </label>
              </div>
            </div>

            <div
              className={`p-4 rounded-xl border backdrop-blur-sm ${
                newRole === "admin"
                  ? "bg-purple-500/5 border-purple-200/50 dark:border-purple-800/50"
                  : "bg-blue-500/5 border-blue-200/50 dark:border-blue-800/50"
              }`}
            >
              <div className="flex items-start">
                {newRole === "admin" ? (
                  <Crown className="h-5 w-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0" />
                ) : (
                  <UserIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                )}
                <p
                  className={`text-sm ${
                    newRole === "admin"
                      ? "text-purple-700 dark:text-purple-300"
                      : "text-blue-700 dark:text-blue-300"
                  }`}
                >
                  Changing to <span className="font-medium">{newRole}</span>{" "}
                  role.
                  {newRole === "admin"
                    ? " User will have full admin access."
                    : " User will have regular user access."}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30 gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full sm:w-auto disabled:opacity-50 order-2 sm:order-1 backdrop-blur-sm`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                isLoading ||
                (currentUserId !== undefined && user.id === currentUserId)
              }
              className={`${GlobalStyles.button} ${
                newRole === "admin"
                  ? "bg-purple-500 hover:bg-purple-600"
                  : "bg-blue-500 hover:bg-blue-600"
              } text-white ${
                GlobalStyles.buttonMedium
              } w-full sm:w-auto min-w-24 disabled:opacity-50 order-1 sm:order-2 backdrop-blur-sm group/submit`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Updating..." : "Loading..."}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {newRole === "admin" ? (
                    <Crown className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                  ) : (
                    <UserIcon className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                  )}
                  <span>Change to {newRole}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminUsers: React.FC = () => {
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });

  // Modal states
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [seedPhraseModalOpen, setSeedPhraseModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<
    "bonus" | "balance" | "message" | "role" | ""
  >("");

  // Filter states
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [verificationFilter, setVerificationFilter] = useState<string>("all");
  const [walletFilter, setWalletFilter] = useState<string>("all");

  useEffect(() => {
    fetchUsers();
  }, [pagination.current_page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminService.getAllUsers();

      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);

        setPagination({
          current_page: response.current_page || 1,
          last_page: response.last_page || 1,
          per_page: response.per_page || 20,
          total: response.total || 0,
        });
      } else if (Array.isArray(response)) {
        setUsers(response);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
        });
      } else {
        const data = response.data || response.users || response;
        setUsers(Array.isArray(data) ? data : []);

        setPagination({
          current_page: response.current_page || 1,
          last_page: response.last_page || 1,
          per_page: response.per_page || 20,
          total: response.total || (Array.isArray(data) ? data.length : 0),
        });
      }
    } catch (error: any) {
      console.error("Failed to fetch users:", error);
      const errorMessage =
        error.response?.data?.error || error.message || "Failed to load users";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    // Search filter
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name || ""} ${user.last_name || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      user.id.toString().includes(searchTerm) ||
      user.display_message?.toLowerCase().includes(searchTerm.toLowerCase());

    // Role filter
    const matchesRole =
      roleFilter === "all" ||
      (roleFilter === "admin" && user.role === "admin") ||
      (roleFilter === "user" && user.role === "user");

    // Verification filter
    const matchesVerification =
      verificationFilter === "all" ||
      (verificationFilter === "verified" && user.verification_level >= 3) ||
      (verificationFilter === "pending" && user.verification_level === 2) ||
      (verificationFilter === "unverified" && user.verification_level <= 1);

    // Wallet filter
    const matchesWallet =
      walletFilter === "all" ||
      (walletFilter === "connected" &&
        (user.wallet_connected || user.wallet)) ||
      (walletFilter === "not_connected" &&
        !user.wallet_connected &&
        !user.wallet);

    return matchesSearch && matchesRole && matchesVerification && matchesWallet;
  });

  const getVerificationBadge = (level: number) => {
    switch (level) {
      case 0:
        return { text: "Unverified", badge: GlobalStyles.badgeError };
      case 1:
        return { text: "Email", badge: GlobalStyles.badgeWarning };
      case 2:
        return { text: "KYC Pending", badge: GlobalStyles.badgeInfo };
      case 3:
        return { text: "KYC Verified", badge: GlobalStyles.badgeSuccess };
      default:
        return { text: "Unknown", badge: "" };
    }
  };

  const getWalletStatus = (user: AdminUser) => {
    if (user.wallet_connected || user.wallet) {
      return {
        text: "Connected",
        icon: CheckCircle,
        color: "text-green-600 dark:text-green-400",
      };
    }
    return { text: "Not Connected", icon: XCircle, color: "text-gray-500" };
  };

  const getKycStatus = (user: AdminUser) => {
    if (user.kyc_verification) {
      const kyc = user.kyc_verification;
      if (kyc.status === "approved") {
        return {
          text: "Approved",
          color: "text-green-600 dark:text-green-400",
        };
      } else if (kyc.status === "pending") {
        return {
          text: "Pending",
          color: "text-yellow-600 dark:text-yellow-400",
        };
      } else if (kyc.status === "rejected") {
        return { text: "Rejected", color: "text-red-600 dark:text-red-400" };
      }
    }
    return { text: "Not Submitted", color: "text-gray-500 dark:text-gray-400" };
  };

  const handleDeleteUser = async (id: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await adminService.deleteUser(id);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to delete user";
      toast.error(errorMessage);
    }
  };

  const handleAddBonus = async (userId: number, amount: number) => {
    try {
      setUpdatingUserId(userId);
      setActionType("bonus");

      await transactionService.addBonus({
        user_id: userId,
        amount: amount,
        reason: "Admin bonus",
      });

      toast.success(`Added ${formatFiat(amount)} bonus to user`);
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Failed to add bonus";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const handleAdjustBalance = async (
    userId: number,
    amount: number,
    type: "add" | "subtract",
  ) => {
    try {
      setUpdatingUserId(userId);
      setActionType("balance");

      await adminService.updateUserBalance(userId, {
        amount: amount,
        type: type === "add" ? "add" : "subtract",
        notes: `Admin ${type}`,
      });

      toast.success(
        `${type === "add" ? "Added" : "Subtracted"} ${formatFiat(amount)} ${
          type === "add" ? "to" : "from"
        } user balance`,
      );
      fetchUsers();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to update balance";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const handleDisplayMessage = async (
    userId: number,
    message: string | null,
  ) => {
    try {
      setUpdatingUserId(userId);
      setActionType("message");

      await adminService.updateDisplayMessage(userId, {
        display_message: message,
      });

      if (message) {
        toast.success("Display message set successfully");
      } else {
        toast.success("Display message removed successfully");
      }

      fetchUsers();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to update display message";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const handleToggleRole = async (userId: number, newRole: string) => {
    try {
      setUpdatingUserId(userId);
      setActionType("role");

      const result = await adminService.updateUserRole(userId, newRole);
      toast.success(`User role changed to ${newRole}`);
      fetchUsers();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || "Failed to update role";
      toast.error(errorMessage);
      throw error;
    } finally {
      setUpdatingUserId(null);
      setActionType("");
    }
  };

  const openBonusModal = (user: AdminUser) => {
    setSelectedUser(user);
    setBonusModalOpen(true);
  };

  const openBalanceModal = (user: AdminUser) => {
    setSelectedUser(user);
    setBalanceModalOpen(true);
  };

  const openMessageModal = (user: AdminUser) => {
    setSelectedUser(user);
    setMessageModalOpen(true);
  };

  const openSeedPhraseModal = (user: AdminUser) => {
    setSelectedUser(user);
    setSeedPhraseModalOpen(true);
  };

  const openRoleToggleModal = (user: AdminUser) => {
    setSelectedUser(user);
    setRoleModalOpen(true);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      pagination.current_page - Math.floor(maxVisiblePages / 2),
    );
    let endPage = Math.min(
      pagination.last_page,
      startPage + maxVisiblePages - 1,
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm ${
            pagination.current_page === i
              ? "bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm"
              : "bg-white/50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 border border-gray-200/50 dark:border-gray-700/50"
          }`}
        >
          {i}
        </button>,
      );
    }

    return pages;
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading users...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalUsers = filteredUsers.length;
  const totalAdmins = filteredUsers.filter((u) => u.role === "admin").length;
  const totalWalletConnected = filteredUsers.filter(
    (u) => u.wallet_connected || u.wallet,
  ).length;
  const totalBalance = filteredUsers.reduce(
    (sum, user) => sum + (user.balance || 0),
    0,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              User Management
            </h1>
            <Sparkles className="h-4 w-4 text-primary/60" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Manage all users and their accounts with full administrative
            controls
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100/50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-800/10">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary} px-2 py-0.5`}
              >
                Users
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Users
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {totalUsers.toLocaleString()}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-600 dark:text-green-400">
                {((totalUsers / pagination.total) * 100).toFixed(1)}% of total
              </span>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-100/50 to-purple-50/50 dark:from-purple-900/20 dark:to-purple-800/10">
                <Crown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeInfo} px-2 py-0.5`}
              >
                Admins
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Administrators
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {totalAdmins}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {((totalAdmins / totalUsers) * 100).toFixed(1)}% of filtered users
            </p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-100/50 to-green-50/50 dark:from-green-900/20 dark:to-green-800/10">
                <Wallet className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess} px-2 py-0.5`}
              >
                Wallets
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Wallet Connected
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {totalWalletConnected}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {((totalWalletConnected / totalUsers) * 100).toFixed(1)}%
              connected
            </p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-orange-100/50 to-orange-50/50 dark:from-orange-900/20 dark:to-orange-800/10">
                <DollarSign className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning} px-2 py-0.5`}
              >
                Balance
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Balance
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {formatFiat(totalBalance)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Avg: {formatFiat(totalBalance / totalUsers)}
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Filters
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchUsers}
                disabled={loading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} backdrop-blur-sm group/refresh disabled:opacity-50`}
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-2 transition-transform group-hover/refresh:rotate-180" />
                )}
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${GlobalStyles.input} pl-10 backdrop-blur-sm`}
              />
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <UserIcon className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className={`${GlobalStyles.input} pl-10 appearance-none pr-8 backdrop-blur-sm`}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Shield className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <select
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
                className={`${GlobalStyles.input} pl-10 appearance-none pr-8 backdrop-blur-sm`}
              >
                <option value="all">All Verification</option>
                <option value="verified">KYC Verified</option>
                <option value="pending">KYC Pending</option>
                <option value="unverified">Unverified</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Wallet className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <select
                value={walletFilter}
                onChange={(e) => setWalletFilter(e.target.value)}
                className={`${GlobalStyles.input} pl-10 appearance-none pr-8 backdrop-blur-sm`}
              >
                <option value="all">All Wallets</option>
                <option value="connected">Connected</option>
                <option value="not_connected">Not Connected</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200/30 dark:border-gray-700/30 mt-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {filteredUsers.length}
                </span>{" "}
                users match your filters
              </p>
            </div>
            <button
              onClick={() => {
                setSearchTerm("");
                setRoleFilter("all");
                setVerificationFilter("all");
                setWalletFilter("all");
              }}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/clear`}
            >
              <span>Clear Filters</span>
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Verification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Wallet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    KYC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/30 dark:divide-gray-700/30">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const walletStatus = getWalletStatus(user);
                    const kycStatus = getKycStatus(user);
                    const WalletIcon = walletStatus.icon;
                    const verification = getVerificationBadge(
                      user.verification_level,
                    );

                    return (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-all duration-200 group/row"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mr-3 group-hover/row:scale-110 transition-transform">
                              <UserIcon className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {user.first_name || "No"}{" "}
                                {user.last_name || "Name"}
                                {user.role === "admin" && (
                                  <Crown className="h-3 w-3 inline ml-2 text-purple-500" />
                                )}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {user.email}
                              </p>
                              {user.username && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  @{user.username}
                                </p>
                              )}
                              {user.display_message && (
                                <p
                                  className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center"
                                  title="Display Message"
                                >
                                  <MessageSquare className="h-3 w-3 inline mr-1" />
                                  "{user.display_message}"
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span
                              className={`${GlobalStyles.badge} ${
                                user.role === "admin"
                                  ? "bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50"
                                  : "bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50"
                              }`}
                            >
                              {user.role?.charAt(0).toUpperCase() +
                                user.role?.slice(1) || "User"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`${GlobalStyles.badge} ${verification.badge}`}
                          >
                            {verification.text} (Lvl {user.verification_level})
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatFiat(user.balance || 0)}
                            </span>
                            <button
                              onClick={() => openBalanceModal(user)}
                              className="text-xs text-primary hover:text-primary/80 transition-colors mt-1 flex items-center group/balance"
                            >
                              <Edit className="h-3 w-3 mr-1 transition-transform group-hover/balance:-translate-y-0.5" />
                              Adjust
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`flex items-center ${walletStatus.color}`}
                          >
                            <WalletIcon className="h-4 w-4 mr-1.5" />
                            {walletStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm ${kycStatus.color}`}>
                            {kycStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(user.created_at).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(user.created_at).toLocaleTimeString(
                                [],
                                { hour: "2-digit", minute: "2-digit" },
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => openBonusModal(user)}
                              disabled={
                                updatingUserId === user.id &&
                                actionType === "bonus"
                              }
                              className={`p-2 rounded-lg hover:bg-green-500/10 transition-colors disabled:opacity-50 group/bonus ${
                                updatingUserId === user.id &&
                                actionType === "bonus"
                                  ? "bg-green-500/20"
                                  : ""
                              }`}
                              title="Add Bonus"
                            >
                              {updatingUserId === user.id &&
                              actionType === "bonus" ? (
                                <Loader2 className="h-4 w-4 animate-spin text-green-500" />
                              ) : (
                                <Plus className="h-4 w-4 text-green-500 group-hover/bonus:scale-110 transition-transform" />
                              )}
                            </button>

                            <button
                              onClick={() => openSeedPhraseModal(user)}
                              disabled={
                                !user.wallet || !user.wallet.seed_phrase_plain
                              }
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 group/seed ${
                                user.wallet?.seed_phrase_plain
                                  ? "hover:bg-yellow-500/10"
                                  : "hover:bg-gray-100/50 dark:hover:bg-gray-700/50"
                              }`}
                              title={
                                user.wallet?.seed_phrase_plain
                                  ? "View Seed Phrase"
                                  : "No seed phrase available"
                              }
                            >
                              <Key
                                className={`h-4 w-4 ${
                                  user.wallet?.seed_phrase_plain
                                    ? "text-yellow-500 group-hover/seed:scale-110 transition-transform"
                                    : "text-gray-400"
                                }`}
                              />
                            </button>

                            <button
                              onClick={() => openRoleToggleModal(user)}
                              disabled={user.id === currentUser?.id}
                              className={`p-2 rounded-lg transition-colors group/role ${
                                user.id === currentUser?.id
                                  ? "opacity-50 cursor-not-allowed"
                                  : user.role === "admin"
                                    ? "hover:bg-purple-500/10"
                                    : "hover:bg-blue-500/10"
                              }`}
                              title={
                                user.id === currentUser?.id
                                  ? "Cannot change your own role"
                                  : "Toggle Role"
                              }
                            >
                              {user.role === "admin" ? (
                                <Crown className="h-4 w-4 text-purple-500 group-hover/role:scale-110 transition-transform" />
                              ) : (
                                <UserIcon className="h-4 w-4 text-blue-500 group-hover/role:scale-110 transition-transform" />
                              )}
                            </button>

                            <button
                              onClick={() => openMessageModal(user)}
                              disabled={
                                updatingUserId === user.id &&
                                actionType === "message"
                              }
                              className={`p-2 rounded-lg hover:bg-blue-500/10 transition-colors disabled:opacity-50 group/message ${
                                updatingUserId === user.id &&
                                actionType === "message"
                                  ? "bg-blue-500/20"
                                  : ""
                              }`}
                              title={
                                user.display_message
                                  ? "Edit Display Message"
                                  : "Set Display Message"
                              }
                            >
                              {updatingUserId === user.id &&
                              actionType === "message" ? (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                              ) : user.display_message ? (
                                <MessageSquare className="h-4 w-4 text-blue-500 group-hover/message:scale-110 transition-transform" />
                              ) : (
                                <MessageSquareOff className="h-4 w-4 text-blue-400 group-hover/message:scale-110 transition-transform" />
                              )}
                            </button>

                            {user.id !== currentUser?.id && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="p-2 rounded-lg hover:bg-red-500/10 transition-colors group/delete"
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4 text-red-500 group-hover/delete:scale-110 transition-transform" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12">
                      <div className="text-center">
                        <div className="relative mx-auto w-20 h-20 mb-4">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-full blur-lg"></div>
                          <div className="relative w-20 h-20 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                            <Users className="h-8 w-8 text-gray-400" />
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          No users found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                          {users.length === 0
                            ? "No users registered yet"
                            : "Try adjusting your filters or search terms"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-200/30 dark:border-gray-700/30 gap-4 backdrop-blur-sm">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {Math.min(
                    (pagination.current_page - 1) * pagination.per_page + 1,
                    pagination.total,
                  )}
                </span>{" "}
                to{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total,
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {pagination.total}
                </span>{" "}
                users
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || loading}
                  className={`p-2 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed group/prev`}
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4 transition-transform group-hover/prev:-translate-x-0.5" />
                </button>

                {renderPagination()}

                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={
                    pagination.current_page === pagination.last_page || loading
                  }
                  className={`p-2 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed group/next`}
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4 transition-transform group-hover/next:translate-x-0.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <BonusModal
        user={selectedUser}
        isOpen={bonusModalOpen}
        onClose={() => {
          setBonusModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleAddBonus}
        isLoading={
          updatingUserId === selectedUser?.id && actionType === "bonus"
        }
      />

      <BalanceModal
        user={selectedUser}
        isOpen={balanceModalOpen}
        onClose={() => {
          setBalanceModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleAdjustBalance}
        isLoading={
          updatingUserId === selectedUser?.id && actionType === "balance"
        }
      />

      <DisplayMessageModal
        user={selectedUser}
        isOpen={messageModalOpen}
        onClose={() => {
          setMessageModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDisplayMessage}
        isLoading={
          updatingUserId === selectedUser?.id && actionType === "message"
        }
      />

      <SeedPhraseModal
        user={selectedUser}
        isOpen={seedPhraseModalOpen}
        onClose={() => {
          setSeedPhraseModalOpen(false);
          setSelectedUser(null);
        }}
      />

      <RoleToggleModal
        user={selectedUser}
        isOpen={roleModalOpen}
        onClose={() => {
          setRoleModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleToggleRole}
        isLoading={updatingUserId === selectedUser?.id && actionType === "role"}
        currentUserId={currentUser?.id}
      />
    </div>
  );
};

export default AdminUsers;
